import {
  Button,
  Switch,
  Radio,
  RadioGroup,
  useDisclosure,
} from "@heroui/react";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { UseFormReturnType } from "@mantine/form";
import { FormValues } from "../../types";
import LimitationsSection from "./LimitationsSection";
import { Language, LimitationTimeframe } from "@/server-types";
import { Languages } from "lucide-react";
import { LocalizationForm } from "@/Components/NextBase/LocalizationForm";
import usePreferredLanguageStore from "@/stores/usePreferredLanguageStore";
import { SUPPORTED_LOCALES } from "../../../../../../../shared/constants/locales";
import { LimeInput } from "@/Components/NextBase/LimeInput";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { LimeNumberInput } from "@/Components/NextBase/LimeNumberInput";

const VariationsTab = ({ form }: { form: UseFormReturnType<FormValues> }) => {
  const { preferredLanguage } = usePreferredLanguageStore((state) => state);
  const defaultLanguage =
    preferredLanguage.userPreferredLanguage ||
    preferredLanguage.clientPreferredLanguage ||
    "sl";

  const {
    isOpen: isNameLocalizationOpen,
    onOpen: onNameLocalizationOpen,
    onClose: onNameLocalizationClose,
  } = useDisclosure();

  const getTimeFrameOptions = () => [
    { key: "day", label: t`Dan` },
    { key: "week", label: t`Teden` },
    { key: "month", label: t`Mesec` },
    { key: "year", label: t`Leto` },
    { key: "all", label: t`Celotno obdobje naročnine` },
  ];

  const handleAddLimitation = (index: number) => {
    const currentLimitations =
      form.values.subscriptionVariations[index].limitations || [];
    const usedTimeframes = new Set(
      currentLimitations.map((limitation) => limitation.timeframe),
    );

    const limitationTimeframes = ["day", "week", "month", "year", "all"];

    const unusedTimeframe =
      limitationTimeframes.find(
        (timeframe) => !usedTimeframes.has(timeframe),
      ) || "month";

    const newLimitation = {
      timeframe: unusedTimeframe,
      limitAmount: 30,
    };

    form.setFieldValue(`subscriptionVariations.${index}.limitations`, [
      ...currentLimitations,
      newLimitation,
    ]);
  };

  const handleRemoveLimitation = (
    parentIndex: number,
    limitationIndex: number,
  ) => {
    const currentLimitations = [
      ...form.values.subscriptionVariations[parentIndex].limitations,
    ];
    currentLimitations.splice(limitationIndex, 1);
    form.setFieldValue(
      `subscriptionVariations.${parentIndex}.limitations`,
      currentLimitations,
    );
  };

  const handleUpdateLimitation = (
    parentIndex: number,
    limitationIndex: number,
    field: "timeframe" | "limitAmount",
    value: LimitationTimeframe | number,
  ) => {
    form.setFieldValue(
      `subscriptionVariations.${parentIndex}.limitations.${limitationIndex}.${field}`,
      value,
    );
  };

  const addEmptyVariation = () => {
    form.insertListItem("subscriptionVariations", {
      nameLocalized: SUPPORTED_LOCALES.map((language: Language) => ({
        language,
        value: "",
      })),
      durationValue: 1,
      durationUnit: "month",
      priceCents: 0,
      isActive: true,
      isRecurring: false,
      limitEntries: false,
      limitations: [],
    });
  };

  return (
    <div className="flex flex-col gap-4">
      {form.values.subscriptionVariations.map((variation, index) => (
        <>
          <div
            key={index}
            className="relative rounded-lg border border-gray-200 p-4 shadow-sm"
          >
            <div className="mb-4">
              <Button
                size="sm"
                variant="bordered"
                className="absolute right-2 top-2"
                onPress={() =>
                  form.removeListItem("subscriptionVariations", index)
                }
              >
                <Trans>Izbriši</Trans>
              </Button>
            </div>
            <div className="flex flex-col gap-4">
              <LimeInput
                type="text"
                variant="bordered"
                label={t`Ime`}
                labelPlacement="outside"
                placeholder={t`Vnesi ime variacije`}
                data-identifier="subscription-variation-name-input"
                {...form.getInputProps(
                  `subscriptionVariations.${index}.nameLocalized`,
                )}
                value={
                  variation.nameLocalized?.find(
                    (n) => n.language === defaultLanguage,
                  )?.value || ""
                }
                onChange={(e) => {
                  const newNameLocalized = [...variation.nameLocalized];
                  const defaultLangIndex = newNameLocalized.findIndex(
                    (n) => n.language === defaultLanguage,
                  );

                  newNameLocalized[defaultLangIndex] = {
                    ...newNameLocalized[defaultLangIndex],
                    value: e.target.value,
                  };
                  form.setFieldValue(
                    `subscriptionVariations.${index}.nameLocalized`,
                    newNameLocalized,
                  );
                }}
                endContent={
                  <Button
                    size="sm"
                    variant="light"
                    isIconOnly
                    onPress={onNameLocalizationOpen}
                  >
                    <Languages size={16} />
                  </Button>
                }
              />

              <div className="flex flex-row gap-4">
                <LimeNumberInput
                  variant="bordered"
                  label={t`Trajanje`}
                  labelPlacement="outside"
                  placeholder="30"
                  minValue={1}
                  step={1}
                  data-identifier="subscription-variation-duration-count"
                  {...form.getInputProps(
                    `subscriptionVariations.${index}.durationValue`,
                  )}
                />
                <LimeSelect
                  variant="bordered"
                  label={t`Enota`}
                  labelPlacement="outside"
                  placeholder={t`Izberi enoto`}
                  data-identifier="subscription-variation-duration-unit"
                  selectedKeys={[variation.durationUnit]}
                  items={getTimeFrameOptions().map((option) => ({
                    label: option.label,
                    key: option.key,
                  }))}
                  {...form.getInputProps(
                    `subscriptionVariations.${index}.durationUnit`,
                  )}
                />
              </div>
              <LimeNumberInput
                variant="bordered"
                label={t`Cena`}
                labelPlacement="outside"
                data-identifier="subscription-variation-price"
                placeholder="30"
                minValue={0}
                {...form.getInputProps(
                  `subscriptionVariations.${index}.priceCents`,
                )}
              />

              <RadioGroup
                size="sm"
                orientation="horizontal"
                value={variation.isRecurring ? "recurring" : "one-time"}
                data-identifier="subscription-variation-recurring"
                onChange={(e) =>
                  form.setFieldValue(
                    `subscriptionVariations.${index}.isRecurring`,
                    e.target.value === "recurring",
                  )
                }
              >
                <Radio value="recurring">
                  <div className="flex flex-col">
                    <span>
                      <Trans>Ponavljajoča naročnina</Trans>
                    </span>
                    <span className="text-sm text-gray-500">
                      <Trans>
                        Naročnina se samodejno obnovi po izteku obdobja
                      </Trans>
                    </span>
                  </div>
                </Radio>
                <Radio value="one-time">
                  <div className="flex flex-col">
                    <span>
                      <Trans>Enkratna naročnina</Trans>
                    </span>
                    <span className="text-sm text-gray-500">
                      <Trans>Naročnina se ne obnovi samodejno</Trans>
                    </span>
                  </div>
                </Radio>
              </RadioGroup>

              <Switch
                size="sm"
                isSelected={variation.limitEntries}
                onValueChange={(value) => {
                  form.setFieldValue(
                    `subscriptionVariations.${index}.limitEntries`,
                    value,
                  );
                }}
              >
                <Trans>Omeji vstope</Trans>
              </Switch>
              {variation.limitEntries && (
                <LimitationsSection
                  parentIndex={index}
                  limitations={variation.limitations}
                  onAddLimitation={handleAddLimitation}
                  onRemoveLimitation={handleRemoveLimitation}
                  onUpdateLimitation={handleUpdateLimitation}
                />
              )}
            </div>
          </div>
          <LocalizationForm
            opened={isNameLocalizationOpen}
            localized={variation.nameLocalized}
            save={(values) => {
              form.setFieldValue(
                `subscriptionVariations.${index}.nameLocalized`,
                values,
              );
              onNameLocalizationClose();
            }}
            cancel={onNameLocalizationClose}
            inputLabel={t`Ime variacije`}
          />
        </>
      ))}
      <div className="flex justify-end">
        <Button size="sm" onPress={addEmptyVariation}>
          <Trans>Dodaj variacijo</Trans>
        </Button>
      </div>
    </div>
  );
};

export default VariationsTab;
